import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import * as Sentry from '@sentry/angular';
import { NativeServiceSyncStatus, SyncResult } from '@shared/models/native-service-syncable';
import { ReplicationSyncStatus } from '@shared/models/replication-sync-status';
import { SalesOrderV3 } from '@shared/models/sales-order-v3';
import { AppSettingsService } from '../app-settings.service';
import { OnlineOfflineService } from '../online-offine.service';
import { SalesOrderV3ReplicationService } from '../sales-order-v3-replication.service';
import { SyncStatusManager } from './sync-status-manager.service';
import { classifySyncFailure, SyncFailure } from './sync-failure';

/**
 * Pushes locally-stored sales orders (Order 3.0) to the Edara backend, sourced
 * entirely from Firestore — never from the Desktop app.
 *
 * Each cycle reads the orders still pending sync, claims them as `Syncing` to
 * guard against concurrent double-sends (the Service Worker runs the same
 * pipeline), posts them to the backend in chunks keyed by `localId`, then marks
 * each order `Synced` on success or rolls it back to `UnSynced` for retry on
 * failure. The run is non-blocking and self-coalescing, so it is safe to invoke
 * from login, reconnect, periodic and post-order triggers.
 *
 * @see US 26848 — Replicate & Restore (Order 3.0), Part 2
 */
@Injectable({ providedIn: 'root' })
export class SalesOrderV3SyncService {
  private readonly http = inject(HttpClient);
  private readonly onlineOfflineService = inject(OnlineOfflineService);
  private readonly salesOrderService = inject(SalesOrderV3ReplicationService);
  private readonly syncStatusManager = inject(SyncStatusManager);

  private readonly edaraCoreBaseUrl: string;
  private readonly entityType = 'salesOrderV3';
  /** Matches the chunk size of the legacy sales-order push. */
  private static readonly CHUNK_SIZE = 10;
  private isSyncing = false;

  constructor() {
    this.edaraCoreBaseUrl = this.normalizeBaseUrl(
      AppSettingsService.appSettings.edaraCoreApi.baseUrl
    );
  }

  /**
   * Push every unsynced sales order to the backend. Non-blocking and
   * self-coalescing — safe to call from the periodic/login/online triggers.
   */
  async pushUnsyncedToServer(): Promise<void> {
    if (this.isSyncing || !this.onlineOfflineService.isOnline) return;

    this.isSyncing = true;
    try {
      const pending = await this.salesOrderService.selectForPushAsync(false);
      if (pending.length === 0) return;

      // Claim the records so a concurrent cycle cannot double-send them.
      const claimed = await this.syncStatusManager.applyStatus(
        this.entityType,
        pending,
        ReplicationSyncStatus.Syncing
      );

      for (let i = 0; i < claimed.length; i += SalesOrderV3SyncService.CHUNK_SIZE) {
        await this.syncChunk(claimed.slice(i, i + SalesOrderV3SyncService.CHUNK_SIZE));
      }
    } catch (error) {
      console.error('[SalesOrderV3Sync] Push failed:', error);
      Sentry.captureException(error);
    } finally {
      this.isSyncing = false;
    }
  }

  private async syncChunk(salesOrders: SalesOrderV3[]): Promise<void> {
    try {
      const results = await firstValueFrom(
        this.http.put<SyncResult[]>(
          `${this.edaraCoreBaseUrl}/api/pos/salesorders/sync`,
          salesOrders
        )
      );
      await this.applyResults(salesOrders, this.extractSyncResults(results));
    } catch (error: any) {
      // Chunk-level failure: a backend rejection becomes `Failed` (with the
      // reason), while a request that never reached the backend stays
      // `UnSynced`. Both are retried — `localId` idempotency makes a re-send
      // safe even if the request actually landed.
      //
      // The chunk-level verdict is only the *fallback*: a batch that carries
      // one rejected order can come back as an HTTP error while its body still
      // holds a verdict for every order, so those are honoured first. Stamping
      // the whole chunk `Failed` regardless is what buried accepted orders
      // under their neighbour's rejection.
      const failure = classifySyncFailure(error);
      await this.applyResults(salesOrders, this.extractSyncResults(error?.error), failure);
      console.error(
        `[SalesOrderV3Sync] Backend sync failed for ${salesOrders.length} order(s); unacknowledged ones marked ${failure.status}.`,
        error
      );
      Sentry.captureException(error);
    }
  }

  /**
   * Read the per-order verdicts out of a sync response — or out of an error
   * body, which carries the same array when the backend answers a partially
   * rejected batch at the HTTP level. Anything that is not a recognisable
   * result list yields `[]`, so the caller falls back to its chunk-level
   * verdict exactly as before.
   */
  private extractSyncResults(body: any): SyncResult[] {
    const candidate = Array.isArray(body)
      ? body
      : body?.results ?? body?.result ?? body?.data ?? body?.syncResults;
    if (!Array.isArray(candidate)) return [];
    return candidate.filter(
      (item: any) => !!item && typeof item.localId === 'string' && item.syncStatus != null
    );
  }

  /**
   * Apply the per-order backend outcome. An order the backend rejected becomes
   * `Failed` (carrying the reason) so the orders grid can surface it; an order
   * simply missing from the response falls back to `unacknowledged` — by
   * default `UnSynced`, since that is a protocol gap rather than a rejection,
   * or the caller's chunk-level verdict when the whole request failed. Both are
   * retried on the next cycle, so nothing is silently lost.
   */
  private async applyResults(
    salesOrders: SalesOrderV3[],
    results: SyncResult[],
    unacknowledged: SyncFailure = {
      status: ReplicationSyncStatus.UnSynced,
      message: 'The backend did not acknowledge the order'
    }
  ): Promise<void> {
    const resultsByLocalId = new Map(results.map(result => [result.localId, result]));

    const succeeded: SalesOrderV3[] = [];
    const rejectedByMessage = new Map<string, SalesOrderV3[]>();
    const unanswered: SalesOrderV3[] = [];

    for (const salesOrder of salesOrders) {
      const result = resultsByLocalId.get(salesOrder.metadata!.localId);
      if (!result) {
        unanswered.push(salesOrder);
        continue;
      }
      // Coerced: the verdict is a numeric enum and must not hinge on the
      // backend serialising it as a number rather than a string.
      if (Number(result.syncStatus) === NativeServiceSyncStatus.Success) {
        succeeded.push(salesOrder);
        continue;
      }
      const message = result.error ?? 'The backend rejected the order';
      const bucket = rejectedByMessage.get(message) ?? [];
      bucket.push(salesOrder);
      rejectedByMessage.set(message, bucket);
    }

    if (succeeded.length) {
      // Empty message, not `undefined`: `applyStatus` leaves the field alone
      // when nothing is passed, which would strand an earlier rejection's text
      // on an order that has since been accepted.
      await this.syncStatusManager.applyStatus(
        this.entityType,
        succeeded,
        ReplicationSyncStatus.Synced,
        ''
      );
    }

    for (const [message, group] of rejectedByMessage) {
      await this.applyFailureStatus(group, ReplicationSyncStatus.Failed, message);
      console.error(
        `[SalesOrderV3Sync] ${group.length} order(s) rejected by the backend; marked Failed (still retried): ${message}`
      );
    }

    if (unanswered.length) {
      await this.applyFailureStatus(unanswered, unacknowledged.status, unacknowledged.message);
      console.warn(
        `[SalesOrderV3Sync] ${unanswered.length} order(s) missing from the sync response; marked ${unacknowledged.status}.`
      );
    }
  }

  /**
   * Apply a non-success verdict without ever downgrading an order the backend
   * has already accepted.
   *
   * `Synced` is documented as terminal, but the same orders are pushed by two
   * pipelines (this service and the Service Worker) whose responses can land
   * out of order, so a late failure verdict could otherwise overwrite a record
   * that is already `Synced`.
   *
   * The Firestore re-read is best-effort and only runs on the failure path
   * (rare by construction): if it cannot confirm a `Synced` record, the verdict
   * is applied exactly as before, so the guard can never swallow a genuine
   * rejection.
   */
  private async applyFailureStatus(
    salesOrders: SalesOrderV3[],
    status: ReplicationSyncStatus,
    message: string
  ): Promise<void> {
    const candidates = salesOrders.filter(
      salesOrder => salesOrder.metadata?.syncInfo?.syncStatus !== ReplicationSyncStatus.Synced
    );

    const checked = await Promise.all(
      candidates.map(async salesOrder => {
        try {
          const stored = await this.salesOrderService.getByLocalIdAsync(
            salesOrder.metadata!.localId
          );
          return stored?.metadata?.syncInfo?.syncStatus === ReplicationSyncStatus.Synced
            ? null
            : salesOrder;
        } catch {
          return salesOrder;
        }
      })
    );

    const toApply = checked.filter((salesOrder): salesOrder is SalesOrderV3 => !!salesOrder);
    const skipped = salesOrders.length - toApply.length;
    if (skipped > 0) {
      console.warn(
        `[SalesOrderV3Sync] Skipped '${status}' for ${skipped} order(s) already accepted by the backend.`
      );
    }
    if (!toApply.length) return;

    await this.syncStatusManager.applyStatus(this.entityType, toApply, status, message);
  }

  private normalizeBaseUrl(url: string): string {
    return url?.endsWith('/') ? url.slice(0, -1) : url;
  }
}
